import {colors} from "@c10h/colors";
/* eslint-disable react-hooks/rules-of-hooks */
import {Trans, useTranslation} from "ni18n";
import React from "react";
import {QA, SectionDivider} from "src/components/v5/Faq/Faq";
import {useFaqIndex} from "src/hooks/useFaqIndex";
import {useTypedSelector} from "src/store";
import {RootStateLocation} from "src/store/types";

import {v5Pages} from "../components/_common/_constants";
import {HeroPropsType} from "../components/v5/Hero";
import {Btn} from "../components/v5/V5Comp";
import {getSchedulingRedirectUrl} from "../utils/getRedirectUrl";
import {LocationId, isAlaskaAirlinesLoc, offersTravelClearanceServices} from "./locations";
import {SpecialtyId} from "./specialtyIds";

type CovidTravelClearanceConstants = {
  heroProps: HeroPropsType;
  scheduleLink: string;
  travelClearanceLocs: RootStateLocation[];
};

export const useCovidTravelClearanceFaqForAlaska = (): (SectionDivider | QA)[] =>
  useFaqIndex([
    "Is Carbon Health configured to meet the travel or entry requirements to any destination?",
    "How much does COVID-19 travel testing cost?",
    "How do I book COVID-19 testing with my family?",
    "How do I add a child or family member who is a minor to my Carbon Health account?",
    "If my partner or family is traveling with me, is it possible to get tested at the same time?",
    "Can I pay with my HSA/FSA Card?",
    "What if my test result is invalid?",
    "How does the COVID-19 Test work?",
    "Where do I find my COVID-19 Test Results?",
    "How do I download my COVID-19 Test Results PDF?",
    "There are no appointments available at my desired testing location. Can I walk in?",
    "Is the test a nucleic acid amplification test (NAAT) test?",
    "How do I find the Portland Airport COVID-19 testing location?",
    "Do I need to have my flight itinerary for a testing appointment?",
    "What type of testing method is required for Japan?",
  ]);

export const useCovidTravelClearanceFaqForAirCanada = (): (SectionDivider | QA)[] =>
  useFaqIndex([
    "Is Carbon Health configured to meet the travel or entry requirements to any destination?",
    "What type of test results are required prior to travel between the US and Canada?",
    "What type of documentation is required prior to boarding an Air Canada flight between the US and Canada?",
    "How do I verify proof of vaccination before re-entering Canada?",
    "What should I do if I'm having difficulty uploading a copy of my vaccination record to ArriveCAN?",
    "How much does COVID-19 travel testing cost?",
    "How do I book COVID-19 testing with my family?",
    "How do I download my COVID-19 Test Results PDF?",
    "Do I need to have my flight itinerary for a testing appointment?",
    "Where do I find my COVID-19 Test Results?",
    "How does the COVID-19 Test work?",
    "If my partner or family is traveling with me, is it possible to get tested at the same time?",
    "What if my test result is invalid?",
    "How to sign up and access test results",
    "What type of testing method is required for Japan?",
  ]);

export const useCovidTravelClearanceConstants = (
  isAlaskaOrAirCanada?: boolean,
): CovidTravelClearanceConstants => {
  const i18n = useTranslation();

  const travelClearanceLocs = useTypedSelector(
    state =>
      state.config.locations &&
      state.config.locations
        .filter(l => offersTravelClearanceServices(l))
        .filter(l => isAlaskaOrAirCanada || !isAlaskaAirlinesLoc(l.id)),
  );

  const scheduleLink = getSchedulingRedirectUrl({
    specialtyId: SpecialtyId.COVID_TESTING_RAPID,
  });

  const heroProps: HeroPropsType = {
    wrapperClasses: "bg-green",
    innerClasses: "maxw75-f minh0-f pv20-f",
    bgImage: "/covidTravel/covid-19-travel-clearance",
    bgImageMobile: "/covidTravel/covid-19-travel-clearance",
    bgImageClasses: "ml120 ml72-md ml40-sm ml0-xs bg-green",
    priority: false,
    head: (
      <h1 className="white maxw35 mt10">
        <Trans>
          <span className="lighterGreen">Take off</span> for your destination safely.
        </Trans>
      </h1>
    ),
    subHead: (
      <p className="mt2 white font-cm fs20 maxw35">
        {i18n.t("Book a COVID-19 clearance test to travel safely. Schedule your test today.")}
      </p>
    ),
    bottom: (
      <div className="mt6 dgc aic gg4 jcs gtcn-md mb8">
        <Btn
          className="fs14-f-md cta-link-to-alaska"
          bg={colors.gray50}
          color={colors.darkerGreen}
          href={v5Pages.covid19TravelClearance}
          link
        >
          {i18n.t("Learn More")}
        </Btn>
        <div className="df fdc gray50 jcc pt1 ml4">
          <span className="fs18 fs16-md font-csb">
            {i18n.t("{{price}} / person", {price: minMaxTravelClearancePrices})}
          </span>
          <p className="font-ir fs14">
            {i18n.t("Pricing at locations varies. Insurance not accepted.")}
          </p>
        </div>
      </div>
    ),
  };

  return {heroProps, scheduleLink, travelClearanceLocs};
};

// Look up of min and max prices per loc
// https://docs.google.com/spreadsheets/d/1snPgT6am2QsriQ39eGioY3_oM0yv6HyZ8N_4HSulM7s/edit?ts=60d3c45a#gid=0
export const travelClearancePricesByLocation: Record<string, [number, number?]> = {
  [LocationId.TRAVEL_CLEARANCE_PORTLAND]: [150, 195],
  [LocationId.TRAVEL_CLEARANCE_BELLEVUE]: [150, 195],
  [LocationId.TRAVEL_CLEARANCE_NORTHGATE_MALL_SEATTLE]: [150, 195],
  [LocationId.TRAVEL_CLEARANCE_NORTHGATE_TUKWILA_WA]: [150, 195],
  [LocationId.TRAVEL_CLEARANCE_LA_COSTA]: [150, 195],
  [LocationId.ALASKA_SAN_DIEGO]: [150, 195],
  [LocationId.TRAVEL_CLEARANCE_ECHO_PARK]: [150, 195],
  [LocationId.TRAVEL_CLEARANCE_SAN_JOSE]: [150, 195],
  default: [60, 195],
};

export const minMaxTravelClearancePrices = travelClearancePricesByLocation
  .vals()
  // @ts-expect-error TS2322: Type '(number | undefined)[]' is not assignable to type 'number[]'.
  .reduce<number[]>((acc, curr) => acc.append(curr), [])
  .sort()
  .reduce<string>(
    (acc, curr, i, arr) =>
      // @ts-expect-error TS2345, TS2345: Argument of type 'number | undefined' is not assignable to parameter of type 'number'.,  Argument of type 'number | undefined' is not assignable to parameter of type 'number'.
      typeof acc === "string" ? acc : `$${Math.min(...arr)} - $${Math.max(...arr)}`,
    // @ts-expect-error TS2345: Argument of type 'null' is not assignable to parameter of type 'string'.
    null,
  );
